import { type Ref, ref, toValue, watchEffect } from 'vue'

import {
  CAMPAIGN_COLUMN_SET_MAP,
  type ColumnSetType,
  modifyColumnsDataOnManualChange,
} from '@/components/CampaignParticipants/CampaignParticipantsColumns.helpers'
import { normalizeCampaignParticipantsColumn } from '@/lib/adapters/campaignParticipants.adapters'
import { handleErrorMessage } from '@/lib/helpers'
import { request } from '@/lib/services'
import { type CurrencyType } from '@/lib/types'
import { useErrorStore } from '@/stores/error.stores'
import { type CampaignType, CampaignTypeEnum } from '@/types'
import {
  type CampaignParticipantType,
  type ParticipantStatus,
} from '@/types/campaignParticipant.types'
import type { ParticipantsColumnType } from '@/types/campaignParticipants.types'

export const useGetCampaignParticipantColumn = (campaign: CampaignType) => {
  const errorStore = useErrorStore()
  const error = ref<Error | null>(null)
  const isLoading = ref(false)

  const data = ref<Partial<Record<ParticipantStatus, ParticipantsColumnType>>>({})
  const columnsList = ref<ParticipantStatus[]>([])
  const columnsLayout = ref<ColumnSetType | null>(null)
  const columnsLoadingStatus = ref<Partial<Record<ParticipantStatus, boolean>>>({})
  const getColumnData = async ({
    page,
    columnType,
    searchQuery,
  }: {
    page: number
    columnType: ParticipantStatus
    searchQuery?: string
  }) => {
    try {
      columnsLoadingStatus.value[columnType] = true
      error.value = null
      const paramsObj = {
        page: page.toString(),
        status: columnType,
        ...(searchQuery && { query: searchQuery }),
      }

      const params = new URLSearchParams(paramsObj).toString()
      const url = `/v2/offers/${campaign.id}/participants?${params}&include[]=offer_comments_count&include[]=has_unread_notifications`

      const response = await request.get(url)
      data.value[columnType] = normalizeCampaignParticipantsColumn(
        columnType,
        response.data,
        page > 1 ? data.value[columnType]?.items : undefined,
      )
    } catch (e: unknown) {
      error.value = e as Error
      errorStore.setError(handleErrorMessage(e))
    } finally {
      columnsLoadingStatus.value[columnType] = false
    }
  }

  const getInitialColumnsData = async (searchQuery?: string) => {
    let columns: ParticipantStatus[] = []
    const hasProductSeeding = campaign.hasProductSeeding
    const columnTypeMap: Record<CampaignTypeEnum, ColumnSetType> = {
      [CampaignTypeEnum.STANDARD]: hasProductSeeding
        ? 'creatorApprovalWithSeeding'
        : 'creatorApproval',
      [CampaignTypeEnum.USER_ACCEPT]: hasProductSeeding
        ? 'creatorApprovalWithSeeding'
        : 'creatorApproval',
      [CampaignTypeEnum.STORE_ACCEPT]: hasProductSeeding
        ? 'advertiserApprovalWithSeeding'
        : 'advertiserApproval',
      [CampaignTypeEnum.SUGGESTION]: hasProductSeeding ? 'proposalWithSeeding' : 'proposal',
    }
    columns = CAMPAIGN_COLUMN_SET_MAP[columnTypeMap[campaign.type]]
    columnsList.value = columns
    columnsLayout.value = columnTypeMap[campaign.type]

    await getAllColumnsData(searchQuery)
  }

  const getAllColumnsData = async (searchQuery?: string) => {
    if (!columnsList.value) {
      return
    }
    isLoading.value = true
    await Promise.all(
      columnsList.value.map((status) => {
        return getColumnData({ page: 1, columnType: status, searchQuery })
      }),
    )
    isLoading.value = false
  }

  const updateColumnItems = ({
    columnStatus,
    items,
    total,
  }: {
    columnStatus: ParticipantStatus
    items: ParticipantsColumnType['items']
    total: number
  }) => {
    if (data.value) {
      data.value[columnStatus] = {
        ...data.value[columnStatus],
        ...{ items, total },
      }
    }
  }

  const updateColumnItem = (
    participant: CampaignParticipantType,
    type: 'addComment' | 'deleteComment' | 'changeFixedFee' | 'todo' | 'clearNotification',
    content?: {
      fixedFee?: number | null
      todo?: { key: string; value: boolean }[]
      currency?: CurrencyType
    },
  ) => {
    const participantId = participant.id
    const columnId = participant.status
    const updatedItem = data.value?.[columnId]?.items.find((item) => item.id == participantId)

    if (updatedItem) {
      switch (type) {
        case 'addComment':
          updatedItem.offerCommentsCount = (updatedItem.offerCommentsCount ?? 0) + 1
          return
        case 'deleteComment':
          updatedItem.offerCommentsCount = (updatedItem.offerCommentsCount ?? 0) - 1
          return
        case 'changeFixedFee':
          if (content?.fixedFee === null) {
            updatedItem.paymentAmountV2 = null
          } else {
            // @ts-expect-error todo fix me
            updatedItem.paymentAmountV2 = updatedItem.paymentAmountV2 || {
              currency: content?.currency?.code,
            }

            updatedItem.paymentAmountV2.amount = content?.fixedFee
          }
          return
        case 'todo':
          updatedItem.todo = content?.todo || null
          return
        case 'clearNotification':
          updatedItem.hasUnreadNotifications = false
      }

      const updatedItems = data.value?.[columnId]?.items.map((i) =>
        i.id === updatedItem.id ? updatedItem : i,
      )
      const updatedColumn = {
        ...data.value?.[columnId],
        ...{ items: updatedItems },
      }
      if (data.value) {
        data.value[columnId] = updatedColumn
      }
    }
  }

  const moveParticipantToColumn = (
    newStatus: ParticipantStatus,
    participant: Ref<CampaignParticipantType>,
    oldStatus?: ParticipantStatus,
  ) => {
    const columnId = oldStatus || participant.value.status
    const participantValue = toValue(participant)
    const columnsDataValue = toValue(data)
    const { updatedFromColumnItems, updatedToColumnItems } = modifyColumnsDataOnManualChange({
      participant: participantValue,
      // @ts-expect-error todo fix me
      columnsData: columnsDataValue,
      newStatus,
      oldStatus: columnId,
    })
    updateColumnItems({
      columnStatus: columnId,
      items: updatedFromColumnItems.items,
      total: updatedFromColumnItems.total,
    })
    updateColumnItems({
      columnStatus: newStatus,
      items: updatedToColumnItems.items,
      total: updatedToColumnItems.total,
    })
  }

  watchEffect(() => void getInitialColumnsData())

  return {
    data,
    columnsList,
    columnsLayout,
    isLoading,
    columnsLoadingStatus,
    error,
    getColumnData,
    getAllColumnsData,
    updateColumnItems,
    updateColumnItem,
    moveParticipantToColumn,
    getInitialColumnsData,
  }
}
